import { useCallback, useRef } from 'react';
import { css } from '@emotion/react';
import { FloatingFocusManager, FloatingArrow } from '@floating-ui/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { useFreeTrialNudgeReturn } from '../hooks/free-trial-nudge-hooks/use-free-trial-tooltip';
import { recordFreetrialNudgeDismiss } from '../utils/free-trial-nudge-utils';
import Award from './assets/Award.svg';
import Report from './assets/Report.svg';

type TrialNudgeBannerProps = Omit<useFreeTrialNudgeReturn, 'isFreeTrialNudgeOpen' | 'triggerProps'>;

export const TrialNudgeBanner = ({ nudgeProps, middleWareData, setFreeTrialNudgeOpenState }: TrialNudgeBannerProps) => {
  const { t } = useTranslation();

  const startFreeTrialRef = useRef<HTMLButtonElement>(null);

  const openSubscriptionPage = useCallback(() => {
    window.location.hash = `settings/subscriptions/feature/weave-insurance-eligibility`;
  }, []);

  const onDismissClick = useCallback(() => {
    setFreeTrialNudgeOpenState(false);
    recordFreetrialNudgeDismiss();
  }, [setFreeTrialNudgeOpenState]);

  return (
    <>
      <div css={trialNudgeBannerStyles}>
        <FloatingFocusManager context={nudgeProps.context} initialFocus={startFreeTrialRef}>
          <div
            {...nudgeProps}
            style={{
              visibility: middleWareData.hide?.referenceHidden ? 'hidden' : 'visible',
              ...nudgeProps.style,
            }}
            ref={nudgeProps.ref}
            className='trial-nudge-banner'
          >
            <FloatingArrow
              ref={nudgeProps.arrowref}
              context={nudgeProps.context}
              fill={theme.colors.secondary.seaweed5}
              width={20}
              height={10}
              strokeWidth={100}
              tipRadius={1}
            />
            <div className='trial-header'>
              <div className='trial-icon'>
                <img src={Award} alt='weave upgrade icon' />
              </div>
              <Heading level={3}>{t('30 Days Free Trial of Weave Insurance Eligibility')}</Heading>
              <Button variant='secondary' onClick={onDismissClick} iconName='x' />
            </div>
            <div className='trial-body'>
              <Text>
                {t(
                  'Reduce claim denials and simplify your workflow with detailed, accurate insurance reports. Start your 30-day free trial now.'
                )}
              </Text>
              <img src={Report} alt='Eligibility Report Preview' className='trial-image' />
            </div>
            <div className='trial-footer'>
              <Button onClick={openSubscriptionPage} ref={startFreeTrialRef}>
                {t('Start Free Trial')}
              </Button>
            </div>
          </div>
        </FloatingFocusManager>
      </div>
    </>
  );
};
const trialNudgeBannerStyles = css`
  .trial-nudge-banner {
    width: 386px;
    background: #fff;
    border-radius: ${theme.borderRadius.medium};
    box-shadow: ${theme.shadows.heavy};
    font-family: Inter, sans-serif;
    color: #1a1a1a;
    padding-bottom: ${theme.spacing(2)};
    z-index: 1000;
  }

  .trial-header {
    display: flex;
    align-items: flex-start;
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing(2)};
    justify-content: space-between;
    background-color: ${theme.colors.secondary.seaweed5};
    height: 96px;
    h3 {
      margin: 0;
    }
  }

  .trial-icon {
    padding-top: ${theme.spacing(0.8)};
    margin-right: ${theme.spacing(2)};
    font-size: 22px;
  }

  .trial-title {
    flex: 1;
    font-weight: 600;
    font-size: 16px;
    margin-left: 8px;
  }

  .trial-body {
    padding: ${theme.spacing(2)};
  }

  .trial-body p {
    font-size: 14px;
    line-height: 1.4;
    margin-bottom: 12px;
  }

  .trial-image {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #eee;
  }

  .trial-footer {
    display: flex;
    justify-content: center;
  }

  .hidden-anchor {
    position: absolute;
    top: 200px;
    left: 500px;
    opacity: 0;
    pointer-events: none;
  }
`;
